import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShoppingBag, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { t } = useLang();
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [error, setError] = useState(null);
  const [justAdded, setJustAdded] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setError(null);
    axios
      .get(`${API}/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setError(t("product.notFound")));
  }, [slug, t]);

  const handleAdd = () => {
    if (!product) return;
    if (product.on_request) {
      window.open(
        `https://wa.me/243981962762?text=${encodeURIComponent(t("product.whatsappQuote", { name: product.name }))}`,
        "_blank"
      );
      return;
    }
    if (product.sizes?.length > 0 && !size) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    addItem(product, { size });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  if (error) {
    return (
      <div className="min-h-screen pt-40 px-6 text-center">
        <p className="font-display text-4xl text-white mb-6">{error}</p>
        <Link to="/divisions" className="btn-gold">{t("product.backToUniverses")}</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-40 px-6">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="aspect-square bg-[#0f0f0f] animate-pulse" />
          <div className="space-y-4">
            <div className="h-4 w-24 bg-[#0f0f0f] animate-pulse" />
            <div className="h-12 w-3/4 bg-[#0f0f0f] animate-pulse" />
            <div className="h-4 w-full bg-[#0f0f0f] animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="pt-28 pb-20">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Link
          to={
            product.category === "sneakers" ? "/sneakers" :
            product.category === "wigs" ? "/perruques" :
            product.category === "couture" ? "/haute-couture" :
            product.category === "epicerie" ? "/epicerie" :
            product.category === "patisserie" ? "/patisserie" : "/divisions"
          }
          data-testid="back-to-collection"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#C5A059] text-sm font-grotesk uppercase tracking-[0.18em] mb-10 transition-colors"
        >
          <ArrowLeft size={14} /> Retour à la collection
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* Images */}
          <div className="md:col-span-7">
            <div className="relative aspect-square bg-[#0a0a0a] border border-white/5 overflow-hidden flex items-center justify-center p-8">
              <img
                src={product.images?.[0]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
              {product.is_limited && (
                <span className="absolute top-6 left-6 bg-[#C5A059] text-black text-[10px] px-3 py-1.5 font-grotesk uppercase tracking-[0.18em] font-semibold">
                  {t("product.limitedEdition")}
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="md:col-span-5 md:sticky md:top-32 md:self-start">
            <p className="overline mb-3">{product.brand}{product.subcategory ? ` · ${product.subcategory}` : ""}</p>
            <h1 data-testid="product-name" className="font-display text-4xl md:text-5xl tracking-tighter text-white leading-[1.05] mb-5">
              {product.name}
            </h1>
            <div className="flex items-baseline gap-3 mb-8">
              <span data-testid="product-price" className="font-display text-3xl text-[#C5A059]">
                {product.on_request ? t("product.onRequest") : `${product.price.toLocaleString("fr-FR")} ${product.currency || "$"}`}
              </span>
              {!product.on_request && product.stock <= 5 && product.stock > 0 && (
                <span className="text-xs text-white/50 uppercase tracking-[0.2em] font-grotesk">
                  · {product.stock} {t("product.inStock")}
                </span>
              )}
            </div>

            <p className="text-[#A1A1AA] leading-relaxed mb-10">{product.description}</p>

            {/* Sizes */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <p className="overline">{t("product.sizeEU")}</p>
                  {!size && (
                    <span data-testid="size-helper" className="text-xs text-[#C5A059] font-grotesk">{t("product.selectSize")}</span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      data-testid={`size-${s}`}
                      onClick={() => setSize(s)}
                      className={`py-3 text-sm font-grotesk border transition-all ${
                        size === s
                          ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]"
                          : "border-white/10 text-white hover:border-white/30"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              data-testid="add-to-cart-button"
              onClick={handleAdd}
              disabled={justAdded}
              className="btn-gold w-full justify-center py-5"
            >
              {product.on_request ? (
                <>
                  <ShoppingBag size={16} /> {t("product.askQuote")}
                </>
              ) : justAdded ? (
                <>
                  <Check size={16} /> {t("product.added")}
                </>
              ) : (
                <>
                  <ShoppingBag size={16} /> {t("product.addToCart")}
                </>
              )}
            </button>

            {/* Story */}
            {product.story && (
              <div className="mt-12 pt-8 border-t border-white/10">
                <p className="overline mb-4">{t("product.story")}</p>
                <p className="text-[#A1A1AA] leading-relaxed italic">{product.story}</p>
              </div>
            )}

            <div className="mt-10 grid grid-cols-2 gap-6 text-xs text-white/50">
              <div>
                <p className="overline mb-2">{t("product.authenticity")}</p>
                <p>{t("product.authenticityDesc")}</p>
              </div>
              <div>
                <p className="overline mb-2">{t("product.delivery")}</p>
                <p>{t("product.deliveryDesc")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
